import { useEffect, useRef } from 'react';
import { clickInfo, createTracker, isNewPageview } from '../services/siteTracker';

const SESSION_GAP_MS = 30 * 60 * 1000;
let activePush = null;

/** Contenu ouvert par le membre (actualité, événement, stage, cours, article de la boutique), compté avec son titre. */
export const trackPortalItem = (kind, id, label) => {
  if (!activePush || !id || !isNewPageview(`item-${kind}`, String(id))) return;
  // Achat boutique : envoi immédiat (la page part ensuite vers le paiement)
  activePush({ type: 'item', kind, item_id: String(id), label: String(label || '').slice(0, 120) }, kind === 'product');
};

/** Compte l'ouverture d'un contenu quand il est affiché et que son titre est connu. */
export const useTrackPortalItem = (kind, id, label, open = true) => {
  const hasLabel = !!label;
  const labelRef = useRef(label);
  labelRef.current = label;
  useEffect(() => {
    if (open && id && hasLabel) trackPortalItem(kind, id, labelRef.current);
  }, [kind, id, open, hasLabel]);
};

/** Portail membre : une « connexion » = ouverture du portail après 30 min sans activité, puis pages (onglets), contenus et clics du membre. */
export const usePortalTracking = ({ enabled, clientId, page }) => {
  const trackerRef = useRef(null);
  const pageRef = useRef(page);
  pageRef.current = page;

  useEffect(() => {
    if (!enabled || !clientId) return undefined;
    const t = createTracker({
      path: '/api/analytics/portal-track',
      headers: () => ({ Authorization: `Bearer ${localStorage.getItem('client_portal_token')}` }),
    });
    const key = `portal_last_activity_${clientId}`;
    // Nouvelle connexion si plus de 30 min depuis la dernière activité de ce membre sur cet appareil
    const push = (ev, now = false) => {
      const ts = Date.now();
      const last = Number(localStorage.getItem(key) || 0);
      localStorage.setItem(key, String(ts));
      if (ts - last > SESSION_GAP_MS) t.push({ type: 'session', page: pageRef.current });
      if (ev) t.push(ev, now);
    };
    trackerRef.current = { push };
    activePush = (ev, now = false) => push({ page: pageRef.current, ...ev }, now);
    push(null);
    if (isNewPageview(`portal-${clientId}`, pageRef.current)) push({ type: 'pageview', page: pageRef.current });
    const onClick = (e) => {
      const info = clickInfo(e);
      if (!info) return;
      const { isLink, ...rest } = info;
      push({ type: 'click', page: pageRef.current, ...rest }, isLink);
    };
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      t.stop();
      trackerRef.current = null;
      activePush = null;
    };
  }, [enabled, clientId]);

  useEffect(() => {
    if (trackerRef.current && isNewPageview(`portal-${clientId}`, page)) trackerRef.current.push({ type: 'pageview', page });
  }, [page, clientId]);
};
